/**
 * MobileUICards.jsx — Premium mobile-first UI cards
 * Fixes: text wrapping, cramped layout, weak shadows, no interactions
 * Style: modern SaaS / fintech / chat-app aesthetic
 */
import React, { useState } from "react";

const F = "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/* ══════════════════════════════════════════════════════════
   1. KwandikaRaporoCard
   Full-width hero card — blue→cyan gradient, proper text flow
══════════════════════════════════════════════════════════ */
export function KwandikaRaporoCard({ onClick, onTemplateClick }) {
  const [pressed, setPressed] = useState(false);

  return (
    <div
      style={{
        position: "relative",
        borderRadius: 20,
        overflow: "hidden",
        background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 45%, #0ea5e9 100%)",
        boxShadow: "0 8px 32px rgba(29,78,216,0.28), 0 2px 8px rgba(0,0,0,0.08)",
        padding: "20px 20px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 0,
        width: "100%",
      }}
    >
      {/* Decorative blobs — purely visual, no layout impact */}
      <span aria-hidden style={{
        position: "absolute", top: -32, right: -32,
        width: 120, height: 120, borderRadius: "50%",
        background: "rgba(255,255,255,0.07)", pointerEvents: "none",
      }} />
      <span aria-hidden style={{
        position: "absolute", bottom: -20, left: 60,
        width: 80, height: 80, borderRadius: "50%",
        background: "rgba(255,255,255,0.05)", pointerEvents: "none",
      }} />

      {/* ── Row 1: icon + title ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <div style={{
          width: 42, height: 42, minWidth: 42, flexShrink: 0,
          borderRadius: 12,
          background: "rgba(255,255,255,0.18)",
          backdropFilter: "blur(8px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        }}>
          <i className="bi bi-file-earmark-text-fill"
            style={{ fontSize: "1.2rem", color: "#fff", lineHeight: 1 }} />
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 style={{
            fontFamily: F, fontWeight: 800, fontSize: "1rem",
            color: "#fff", margin: 0, lineHeight: 1.25,
            /* Never break — title is short enough */
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            Kwandika Raporo
          </h2>
          <p style={{
            fontFamily: F, fontSize: "0.72rem", fontWeight: 500,
            color: "rgba(255,255,255,0.65)", margin: "2px 0 0",
            whiteSpace: "nowrap",
          }}>
            Andika · Sinyisha · Bika
          </p>
        </div>
      </div>

      {/* ── Row 2: description — full width, proper word-wrap ── */}
      <p style={{
        fontFamily: F,
        fontSize: "0.83rem",
        fontWeight: 400,
        color: "rgba(255,255,255,0.85)",
        margin: 0,
        lineHeight: 1.6,
        /* KEY FIX: let words wrap naturally, never break mid-word */
        wordBreak: "normal",
        overflowWrap: "break-word",
        whiteSpace: "normal",
        maxWidth: "100%",
      }}>
        Andika cyangwa ushyiremo umwirondoro.
      </p>

      {/* ── Row 3: CTA button ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: 2 }}>
        {/* Template shortcut — ghost pill */}
        <button
          onClick={onTemplateClick}
          style={{
            fontFamily: F, fontSize: "0.72rem", fontWeight: 600,
            color: "rgba(255,255,255,0.75)",
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 20, padding: "5px 12px",
            cursor: "pointer", whiteSpace: "nowrap",
            transition: "background 0.15s, color 0.15s",
            display: "flex", alignItems: "center", gap: 5,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = "rgba(255,255,255,0.22)";
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = "rgba(255,255,255,0.12)";
            e.currentTarget.style.color = "rgba(255,255,255,0.75)";
          }}
        >
          <i className="bi bi-layout-text-sidebar" style={{ fontSize: "0.75rem" }} />
          Inyandikorugero
        </button>

        {/* Primary CTA */}
        <button
          onClick={onClick}
          onMouseDown={() => setPressed(true)}
          onMouseUp={() => setPressed(false)}
          onMouseLeave={() => setPressed(false)}
          onTouchStart={() => setPressed(true)}
          onTouchEnd={() => setPressed(false)}
          style={{
            fontFamily: F, fontWeight: 700, fontSize: "0.82rem",
            color: "#1d4ed8",
            background: "#fff",
            border: "none",
            borderRadius: 12,
            padding: "9px 20px",
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
            display: "flex", alignItems: "center", gap: 6,
            boxShadow: pressed
              ? "0 1px 4px rgba(0,0,0,0.1)"
              : "0 4px 16px rgba(0,0,0,0.18)",
            transform: pressed ? "scale(0.97)" : "scale(1)",
            transition: "transform 0.12s, box-shadow 0.12s",
          }}
          onMouseEnter={e => {
            if (!pressed) {
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.22)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }
          }}
          onMouseLeave={e => {
            e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.18)";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <i className="bi bi-arrow-right-circle-fill" style={{ fontSize: "0.88rem" }} />
          Hita mo
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   2. OngerahoUmukonoCard
   White card — icon left, text center, action right
   Smooth signed/unsigned state transition
══════════════════════════════════════════════════════════ */
export function OngerahoUmukonoCard({ sigData, onClick }) {
  const signed = !!sigData;
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: signed
          ? "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)"
          : "#fff",
        borderRadius: 16,
        padding: "14px 16px",
        border: signed ? "1.5px solid #86efac" : "1.5px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        gap: 12,
        minWidth: 0,
        overflow: "hidden",
        boxShadow: signed
          ? "0 4px 16px rgba(21,128,61,0.1)"
          : "0 2px 12px rgba(0,0,0,0.06)",
        transition: "border-color 0.25s, box-shadow 0.25s, background 0.25s",
      }}
    >
      {/* Icon container */}
      <div style={{
        width: 44, height: 44, minWidth: 44, flexShrink: 0,
        borderRadius: 12,
        background: signed ? "rgba(21,128,61,0.12)" : "rgba(29,78,216,0.08)",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "background 0.25s",
      }}>
        <i
          className={signed ? "bi bi-patch-check-fill" : "bi bi-pen-fill"}
          style={{
            fontSize: "1.2rem",
            color: signed ? "#15803d" : "#2563eb",
            transition: "color 0.25s",
          }}
        />
      </div>

      {/* Text — flex:1 with minWidth:0 is the critical fix for overflow */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontFamily: F, fontWeight: 700, fontSize: "0.88rem",
          color: signed ? "#15803d" : "#1e293b",
          margin: 0, lineHeight: 1.3,
          /* Allow normal wrapping — no forced nowrap on body text */
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          transition: "color 0.25s",
        }}>
          {signed ? "Umukono Wabitswe" : "Ongeraho Umukono"}
        </p>
        <p style={{
          fontFamily: F, fontSize: "0.73rem", fontWeight: 400,
          color: signed ? "#16a34a" : "#64748b",
          margin: "3px 0 0", lineHeight: 1.4,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          transition: "color 0.25s",
        }}>
          {signed ? "✓ Washyizweho neza — kanda guhindura" : "Kanda hano gushyiraho umukono wawe"}
        </p>
      </div>

      {/* Action button */}
      <button
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          fontFamily: F, fontWeight: 700, fontSize: "0.78rem",
          flexShrink: 0,
          borderRadius: 10,
          padding: "8px 14px",
          cursor: "pointer",
          whiteSpace: "nowrap",
          display: "flex", alignItems: "center", gap: 5,
          transition: "all 0.18s",
          ...(signed ? {
            color: "#15803d",
            background: hovered ? "rgba(21,128,61,0.15)" : "rgba(21,128,61,0.08)",
            border: "1.5px solid #86efac",
          } : {
            color: "#fff",
            background: hovered
              ? "linear-gradient(135deg, #1e40af, #0284c7)"
              : "linear-gradient(135deg, #2563eb, #0ea5e9)",
            border: "none",
            boxShadow: hovered ? "0 4px 14px rgba(37,99,235,0.35)" : "0 2px 8px rgba(37,99,235,0.2)",
          }),
        }}
      >
        <i className={signed ? "bi bi-pencil-square" : "bi bi-vector-pen"}
          style={{ fontSize: "0.82rem" }} />
        {signed ? "Hindura" : "Sinyisha"}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   3. IzinaRyaDosiyeCard
   File info row — icon · label+name · copy button
   Handles long filenames gracefully with ellipsis
══════════════════════════════════════════════════════════ */
export function IzinaRyaDosiyeCard({ fileName }) {
  const display = fileName ? `${fileName}.pdf` : "raporo.pdf";
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    try { navigator.clipboard?.writeText(display); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div style={{
      background: "#fff",
      borderRadius: 14,
      padding: "10px 14px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
      overflow: "hidden",
    }}>
      {/* File icon */}
      <div style={{
        width: 36, height: 36, minWidth: 36, flexShrink: 0,
        borderRadius: 10,
        background: "rgba(239,68,68,0.08)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className="bi bi-file-earmark-pdf-fill"
          style={{ fontSize: "1rem", color: "#ef4444" }} />
      </div>

      {/* Label + filename — flex:1 + minWidth:0 prevents overflow */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          fontFamily: F, fontSize: "0.6rem", fontWeight: 700,
          color: "#94a3b8", textTransform: "uppercase",
          letterSpacing: "0.07em", display: "block",
        }}>
          Izina rya Dosiye
        </span>
        <span style={{
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
          fontSize: "0.76rem", fontWeight: 600, color: "#334155",
          display: "block", marginTop: 1,
          /* Ellipsis on long names — never wraps */
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {display}
        </span>
      </div>

      {/* Copy button with feedback */}
      <button
        onClick={handleCopy}
        title={copied ? "Byakopiwemo!" : "Kopi izina"}
        style={{
          width: 32, height: 32, minWidth: 32, flexShrink: 0,
          borderRadius: 8,
          border: copied ? "1px solid #86efac" : "1px solid #e2e8f0",
          background: copied ? "#f0fdf4" : "#f8fafc",
          color: copied ? "#15803d" : "#64748b",
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.2s",
          fontSize: "0.82rem",
        }}
        onMouseEnter={e => {
          if (!copied) {
            e.currentTarget.style.background = "#eff6ff";
            e.currentTarget.style.color = "#2563eb";
            e.currentTarget.style.borderColor = "#bfdbfe";
          }
        }}
        onMouseLeave={e => {
          if (!copied) {
            e.currentTarget.style.background = "#f8fafc";
            e.currentTarget.style.color = "#64748b";
            e.currentTarget.style.borderColor = "#e2e8f0";
          }
        }}
      >
        <i className={copied ? "bi bi-check-lg" : "bi bi-clipboard"} />
      </button>
    </div>
  );
}
